import { notifications } from '@mantine/notifications';

// Shape of the `pages/:pageId/recommender-limits` response and of the 422 body,
// as fixed by the magellan-api `improvement/recommender-page-limits` contract.
// A `limit` of null means the domain has that limit lifted, and
// `distinctFeedsPerPage` only applies while the domain is a large catalog.
const RECOMMENDER_PAGE_LIMIT_CODE = 'RECOMMENDER_PAGE_LIMIT';

const WARNING_RATIO = 0.8;

const applicableLimits = (usage) =>
  [
    { kind: 'recommendersPerPage', applies: true, ...usage?.recommendersPerPage },
    { kind: 'distinctFeedsPerPage', ...usage?.distinctFeedsPerPage },
  ].filter(({ applies, limit }) => applies && typeof limit === 'number');

export const recommenderPageLimitLevel = (usage) => {
  if (!usage) {
    return null;
  }

  if (usage.overLimit) {
    return 'error';
  }

  if (recommenderPageLimitReason(usage)) {
    return 'reached';
  }

  return applicableLimits(usage).some(({ current, limit }) => current / limit >= WARNING_RATIO) ? 'warning' : null;
};

const limitSentence = ({ kind, current, limit }) =>
  kind === 'distinctFeedsPerPage'
    ? `The product blocks running on this page already use ${current} of the ${limit} product feeds allowed on a large catalog.`
    : `This page already runs ${current} of ${limit} product blocks.`;

// Why an "add"/"enable" action is blocked, or null when nothing is at its limit
// — which is also what keeps those actions enabled.
export const recommenderPageLimitReason = (usage) => {
  const reached = applicableLimits(usage).find(({ current, limit }) => current >= limit);

  if (!reached) {
    return null;
  }

  return `${limitSentence(reached)} Turn one off, or ask a super admin to raise the limit in the domain settings.`;
};

export const isRecommenderPageLimitError = (error) => error?.code === RECOMMENDER_PAGE_LIMIT_CODE;

export const showRecommenderPageLimitNotification = (error) => {
  // The contract does not require the API's message to carry the numbers, so
  // they are added whenever the message leaves them out.
  const hasNumbers = typeof error.limit === 'number' && typeof error.current === 'number';
  const messageHasNumbers =
    hasNumbers && error.message?.includes(String(error.limit)) && error.message.includes(String(error.current));
  const details = [error.message, hasNumbers && !messageHasNumbers ? limitSentence(error) : null].filter(Boolean);

  notifications.show({
    color: 'red',
    title: 'Product block limit reached',
    message: `${details.join(' ')} Turn off a product block, or ask a super admin to raise the limit in the domain settings.`,
    autoClose: 10000,
  });
};
